// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext, Parliamentarian } from '../types';
import { histogramBins, niceMax, axisTicks } from '../charts';
import { fmtMoney, fmtNumber } from '../format';
import { statCard } from '../ui';

function chamberPass(p: Parliamentarian, c: string): boolean {
  if (c === 'all') return true;
  if (c === 'parl') return p.chamber === 'House' || p.chamber === 'Senate';
  return p.chamber === c;
}

export function renderDistribution(root: HTMLElement, ctx: AppContext): void {
  const { data } = ctx;
  let chamber = 'parl';

  root.innerHTML = `
    <div class="view-head">
      <h2>The spread</h2>
      <p>How per-parliamentarian totals are distributed. Most cluster in a normal band; a long right tail of high spenders (largely ministers and leaders, who travel far more) pulls the average well above the median.</p>
    </div>
    <div class="filters">
      <div class="filter"><label>Group</label>
        <div class="chip-toggle" id="d-chamber">
          <button data-c="parl" class="active">MPs &amp; Senators</button>
          <button data-c="House">House</button>
          <button data-c="Senate">Senate</button>
        </div></div>
    </div>
    <div class="stat-row" id="d-stats"></div>
    <div class="chart-card"><h3>Distribution of total expenses</h3><div class="subtitle" id="d-sub"></div><div id="d-hist"></div></div>`;

  const draw = () => {
    const people = data.people.filter((p) => chamberPass(p, chamber) && p.total > 0);
    const totals = people.map((p) => p.total);
    const sorted = [...totals].sort((a, b) => a - b);
    const n = sorted.length;
    const median = n ? (n % 2 ? sorted[(n - 1) / 2] : (sorted[n / 2 - 1] + sorted[n / 2]) / 2) : 0;
    const mean = n ? totals.reduce((s, x) => s + x, 0) / n : 0;
    const p90 = n ? sorted[Math.floor(0.9 * (n - 1))] : 0;

    document.getElementById('d-stats')!.innerHTML =
      statCard('People', fmtNumber(n)) +
      statCard('Median', fmtMoney(median), 'half spend less than this') +
      statCard('Average', fmtMoney(mean), 'pulled up by big spenders') +
      statCard('90th percentile', fmtMoney(p90), '1 in 10 spend more');
    document.getElementById('d-sub')!.innerHTML =
      `Each bar counts how many people fall in that spending band. The dashed line marks the median (${fmtMoney(median)}); bars beyond twice the median are highlighted as outliers.`;

    drawHist(document.getElementById('d-hist')!, totals, median);
  };

  document.getElementById('d-chamber')!.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest<HTMLElement>('button[data-c]');
    if (!btn) return;
    chamber = btn.dataset.c!;
    document.querySelectorAll('#d-chamber button').forEach((b) => b.classList.toggle('active', b === btn));
    draw();
  });

  draw();
}

function drawHist(el: HTMLElement, values: number[], median: number): void {
  const W = 900, H = 340, ml = 44, mr = 12, mt = 14, mb = 54;
  const iw = W - ml - mr, ih = H - mt - mb;
  const maxVal = Math.max(...values, 1);
  const bins = histogramBins(values, 22, 0, niceMax(maxVal));
  const maxCount = Math.max(...bins.map((b) => b.count), 1);
  const yMax = niceMax(maxCount);
  const bw = (iw / bins.length) * 0.9;
  const ticks = axisTicks(yMax, 4);
  const grid = ticks
    .map((t) => {
      const y = mt + ih - (t / yMax) * ih;
      return `<line class="grid-line" x1="${ml}" y1="${y}" x2="${W - mr}" y2="${y}"></line><text class="axis-label" x="${ml - 6}" y="${y + 3}" text-anchor="end">${Math.round(t)}</text>`;
    })
    .join('');
  const bars = bins
    .map((b, i) => {
      const x = ml + (iw / bins.length) * i + ((iw / bins.length) - bw) / 2;
      const h = (b.count / yMax) * ih;
      const y = mt + ih - h;
      const outlier = b.x0 >= median * 2;
      const tip = `${fmtMoney(b.x0)}–${fmtMoney(b.x1)}\n${b.count} ${b.count === 1 ? 'person' : 'people'}`;
      return `<rect class="bar-el" x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${bw.toFixed(1)}" height="${Math.max(0, h).toFixed(1)}" rx="1.5" fill="${outlier ? '#b26a00' : '#127a4f'}" data-tip="${tip}" aria-label="${tip}"></rect>`;
    })
    .join('');
  // x-axis labels every ~4 bins
  const xlabels = bins
    .map((b, i) => (i % 4 === 0 ? `<text class="axis-label" x="${(ml + (iw / bins.length) * i).toFixed(1)}" y="${H - mb + 18}" text-anchor="middle">${fmtMoney(b.x0)}</text>` : ''))
    .join('');
  const medX = ml + (Math.min(1, median / niceMax(maxVal))) * iw;
  const medLine = `<line x1="${medX.toFixed(1)}" y1="${mt}" x2="${medX.toFixed(1)}" y2="${mt + ih}" stroke="#1c3a5e" stroke-width="1.5" stroke-dasharray="4 3"></line><text class="axis-label" x="${medX.toFixed(1)}" y="${mt + 10}" text-anchor="middle" fill="#1c3a5e">median ${fmtMoney(median)}</text>`;
  el.innerHTML = `<svg class="chart" viewBox="0 0 ${W} ${H}" role="img" aria-label="Histogram of total expenses">${grid}<line class="axis-line" x1="${ml}" y1="${mt + ih}" x2="${W - mr}" y2="${mt + ih}"></line>${bars}${medLine}${xlabels}<text class="axis-label" x="${(ml + iw / 2).toFixed(1)}" y="${H - 6}" text-anchor="middle">Total expenses since 2022 →</text></svg>`;
}
